import moment from 'moment-timezone'

import { getRandomMinuteInterval } from './utils'

import recommendations from '@/graphql/queries/recommendations.gql'

/**
 * Returns an Array of the next schedule dates for the given subreddit schedule.
 * @param {Object[]} subredditSchedule - The array of schedule objects.
 * @param {string} lastScheduledSubmissionDate - The last scheduled submission date in ISO-8601 format.
 * @returns {Array} - The array of next schedule dates.
 */
export function getScheduledDatesFromLastSubmission(
  subredditSchedule,
  lastScheduledSubmissionDate
) {
  let now = moment().seconds(0).milliseconds(0)
  let initialDate = now.clone()
  if (lastScheduledSubmissionDate) {
    let lastSubmissionDate = moment(lastScheduledSubmissionDate)
    if (lastSubmissionDate.isAfter(initialDate)) {
      initialDate = lastSubmissionDate.clone()
    }
  }

  return subredditSchedule
    .map((schedule) => {
      // Split time string in the format HH:mm
      const parts = schedule.time.split(':')
      const hour = parseInt(parts[0])
      const minutes = parseInt(parts[1])

      let date = initialDate.clone().isoWeekday(schedule.day).set({
        hour: hour,
        minute: minutes
      })
      if (date.isSameOrBefore(initialDate)) {
        date = date.add(1, 'week')
      }

      return date
    })
    .sort((a, b) => {
      return a.diff(b)
    })
}

/**
 * Returns an Array of the next schedule dates for the given subreddit schedule.
 * This function will return all scheduled dates, including any gaps, up to
 * 4 weeks after the last scheduled submission.
 * @param {Object[]} subredditSchedule - The array of schedule objects.
 * @param {Object[]} scheduledSubmissions - The array of already scheduled submissions for this subreddit.
 * @returns {Array} - The array of next schedule dates.
 */
export function getNextScheduleDates(subredditSchedule, scheduledSubmissions) {
  let now = moment().seconds(0).milliseconds(0)

  let startDate = now.clone()

  let endDate = now.clone()
  if (scheduledSubmissions.length > 0) {
    let lastScheduledDate = moment(
      scheduledSubmissions[scheduledSubmissions.length - 1].postAt
    )
    if (lastScheduledDate.isAfter(endDate)) {
      endDate = lastScheduledDate
    }
  }
  endDate = endDate.add(4, 'weeks')

  let dates = []
  let currentDate = startDate.clone()

  // Start schedule index from the next available day
  let scheduleIndex = 0
  for (let schedule of subredditSchedule) {
    const parts = schedule.time.split(':')
    const hour = parseInt(parts[0])
    const minutes = parseInt(parts[1])

    let date = currentDate.clone().isoWeekday(schedule.day).set({
      hour: hour,
      minute: minutes
    })
    if (date.isSameOrAfter(currentDate)) {
      break
    }

    scheduleIndex++
  }

  if (scheduleIndex >= subredditSchedule.length) {
    scheduleIndex = 0
  }

  while (currentDate.isBefore(endDate)) {
    // Split time string in the format HH:mm
    let schedule = subredditSchedule[scheduleIndex]
    const parts = schedule.time.split(':')
    const hour = parseInt(parts[0])
    const minutes = parseInt(parts[1])

    let date = currentDate.clone().isoWeekday(schedule.day).set({
      hour: hour,
      minute: minutes
    })
    if (date.isSameOrBefore(currentDate)) {
      date = date.add(1, 'week')
    }

    const hasScheduledSubmissionForDateAndTime = scheduledSubmissions.find(
      (submission) => moment(submission.postAt).isSame(date, 'minute')
    )
    const hasScheduledSubmissionForDate = scheduledSubmissions.find(
      (submission) => moment(submission.postAt).isSame(date, 'day')
    )
    const hasMultipleSchedulesForWeekday =
      subredditSchedule.filter((s) => s.day === schedule.day).length > 1

    if (
      !hasScheduledSubmissionForDateAndTime &&
      (!hasScheduledSubmissionForDate || hasMultipleSchedulesForWeekday)
    ) {
      dates.push(date)
    }

    scheduleIndex = (scheduleIndex + 1) % subredditSchedule.length
    currentDate = date.clone()
  }

  return dates.sort((a, b) => {
    return a.diff(b)
  })
}

// Regexes for parsing recommendation expressions.
const BEST_TODAY_REGEX = /(\d+)?best_today([+-]\d+[mh])?/
const BEST_TOMORROW_REGEX = /(\d+)?best_tomorrow([+-]\d+[mh])?/
const BEST_RELATIVE_REGEX = /(\d+)?best_(\d*)_days([+-]\d+[mh])?/
const BEST_ABSOLUTE_REGEX =
  /(\d+)?best_(\d\d\d\d)-(\d\d?)-(\d\d?)([+-]\d+[mh])?/
const BEST_RANGE_REGEX = /(\d+)?best_next_(\d*)_days([+-]\d+[mh])?/

const RECOMMENDATION_REGEXES = [
  BEST_TODAY_REGEX,
  BEST_TOMORROW_REGEX,
  BEST_RELATIVE_REGEX,
  BEST_ABSOLUTE_REGEX,
  BEST_RANGE_REGEX
]

export function validateRecommendationExpression(recommendation) {
  return RECOMMENDATION_REGEXES.some((regex) => recommendation.match(regex))
}

export async function getPostAtFromRecommendation(
  subreddit,
  recommendation,
  userTimezone,
  $apollo
) {
  const response = await $apollo.query({
    query: recommendations,
    variables: {
      subreddit: subreddit,
      timezone: userTimezone
    }
  })
  const analytics = response.data.analytics

  // Subreddit must not exist. Return early and let validation handle this.
  if (!analytics.subreddit) {
    return
  }

  // Get the date from the recommendation
  const startDate = moment()
  let endDate
  let bestDate
  let bestDateTimes
  let offset = ''
  let recommendedIndex = 0
  const bestTodayMatch = recommendation.match(BEST_TODAY_REGEX)
  const bestTomorrowMatch = recommendation.match(BEST_TOMORROW_REGEX)
  const relativeRegexMatch = recommendation.match(BEST_RELATIVE_REGEX)
  const absoluteRegexMatch = recommendation.match(BEST_ABSOLUTE_REGEX)
  const bestRangeRegexMatch = recommendation.match(BEST_RANGE_REGEX)

  // Check all the regexes to see if they matched
  if (bestTodayMatch) {
    recommendedIndex = Number(bestTodayMatch[1] || 1) - 1
    offset = bestTodayMatch[2] || ''
    bestDate = startDate
    endDate = bestDate.clone().endOf('day')
    bestDateTimes = getBestDateTimesForDate(analytics.posts, bestDate)
  } else if (bestTomorrowMatch) {
    recommendedIndex = Number(bestTomorrowMatch[1] || 1) - 1
    offset = bestTomorrowMatch[2] || ''
    bestDate = startDate.clone().add(1, 'day')
    endDate = bestDate.clone().endOf('day')
    bestDateTimes = getBestDateTimesForDate(analytics.posts, bestDate)
  } else if (relativeRegexMatch) {
    recommendedIndex = Number(relativeRegexMatch[1] || 1) - 1
    offset = relativeRegexMatch[3] || ''
    bestDate = startDate.clone().add(Number(relativeRegexMatch[2]), 'days')
    endDate = bestDate.clone().endOf('day')
    bestDateTimes = getBestDateTimesForDate(analytics.posts, bestDate)
  } else if (absoluteRegexMatch) {
    recommendedIndex = Number(absoluteRegexMatch[1] || 1) - 1
    const year = absoluteRegexMatch[2]
    const month = absoluteRegexMatch[3]
    const date = absoluteRegexMatch[4]
    offset = absoluteRegexMatch[5] || ''
    bestDate = moment()
      .year(Number(year))
      .month(Number(month) - 1)
      .date(Number(date))
    endDate = bestDate.clone().endOf('day')
    bestDateTimes = getBestDateTimesForDate(analytics.posts, bestDate)
  } else if (bestRangeRegexMatch) {
    // The one and only range match (so far)
    recommendedIndex = Number(bestRangeRegexMatch[1] || 1) - 1
    let daysFromNow = Number(bestRangeRegexMatch[2] || 7)
    offset = bestRangeRegexMatch[3] || ''
    endDate = startDate.clone().add(daysFromNow, 'days').endOf('day')

    if (daysFromNow > 7) {
      // If the range is bigger than 7 days, we adjust the range so that
      // it only includes the last 7 days.
      startDate.add(daysFromNow - 7, 'days')
    }
    bestDateTimes = getBestDateTimesForRange(
      analytics.posts,
      startDate,
      endDate
    )
  } else {
    throw new Error(`recommendation ${recommendation} is invalid`)
  }

  if (!bestDateTimes.length) {
    return
  }
  // Make sure we don't get an index error
  if (recommendedIndex >= bestDateTimes.length) {
    recommendedIndex = 0
  }

  let bestDateTime = bestDateTimes[recommendedIndex]

  if (offset) {
    let add = offset.startsWith('+')
    let amount = Number(offset.match(/\d+/)[0])
    let unit = offset[offset.length - 1]

    if (add) {
      let transformedDateTime = bestDateTime.clone().add(amount, unit)
      if (transformedDateTime.isSameOrBefore(endDate)) {
        bestDateTime = transformedDateTime
      }
    } else {
      let transformedDateTime = bestDateTime.clone().subtract(amount, unit)
      if (transformedDateTime.isAfter(startDate)) {
        bestDateTime = transformedDateTime
      }
    }
  }

  if (bestDateTime.isSameOrBefore(moment())) {
    bestDateTime.add('1', 'week')
  }
  return bestDateTime.toDate()
}

export function getBestDayTimesForDate(analyticsPosts, bestDate) {
  return analyticsPosts
    .filter((post) => convertDay(post.day) === bestDate.day())
    .sort((a, b) => b.posts - a.posts)
}

/**
 * Returns an array of moment objects with the best date times for the given date,
 * based on the post analytics.
 */
export function getBestDateTimesForDate(analyticsPosts, bestDate) {
  const bestDayTimes = getBestDayTimesForDate(analyticsPosts, bestDate)
  const bestDateTimes = []
  for (let bestDayTime of bestDayTimes) {
    const bestDateTime = bestDate
      .clone()
      .hour(bestDayTime.hour)
      .minute(getRandomMinuteInterval())

    if (bestDateTime.isAfter(moment())) {
      bestDateTimes.push(bestDateTime)
    }
  }
  return bestDateTimes
}

/**
 * Returns an array of moment objects with the best date times for the given range,
 * based on the post analytics.
 */
export function getBestDateTimesForRange(analyticsPosts, startDate, endDate) {
  const dates = listDatesBetweenDates(startDate, endDate)
  const days = dates.map((d) => d.day())
  const bestDayTimes = analyticsPosts
    .filter((post) => days.includes(convertDay(post.day)))
    .sort((a, b) => b.posts - a.posts)

  const bestDateTimes = []
  for (let bestDayTime of bestDayTimes) {
    const bestDate = dates.find((d) => d.day() === convertDay(bestDayTime.day))
    const bestDateTime = bestDate
      .clone()
      .hour(bestDayTime.hour)
      .minute(getRandomMinuteInterval())

    if (bestDateTime.isAfter(moment())) {
      bestDateTimes.push(bestDateTime)
    }
  }
  return bestDateTimes
}

export function listDatesBetweenDates(startDate, endDate) {
  let current = startDate.clone()
  let dates = []

  while (current.isSameOrBefore(endDate)) {
    dates.push(current.clone())
    current.add(1, 'days')
  }
  return dates
}

export function convertDay(dayIndex) {
  if (dayIndex === 6) {
    return 0
  } else {
    return dayIndex + 1
  }
}
